import type { ScreenerData } from "epsm-api-types/models";
import { useMemo } from "react";
import { formatValueDate } from "@/components/widgets/helpers/FormattingHelpers";
import { componentClassNames } from "@/components/widgets/Quote/config";
import { formatPositivePercentValue } from "@/components/widgets/Quote/lib/helpers/formatPositivePercentValue";
import { formatEps } from "@/components/widgets/Quote/lib/helpers/quoteValueFormatters";
import { QuoteRow } from "@/components/widgets/Quote/ui/components";
import { LineHeader } from "@/components/widgets/Quote/ui/components/LineHeader";
import { valueColor } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { cn } from "@/lib/utils";

export const EarningsTable = ({
	screenerData,
}: {
	screenerData: ScreenerData | null;
}) => {
	const {
		act_rpt_date,
		eps_growth,
		sales_growth,
		combined_acceleration_curr_and_1_q_back,
		combined_sequential_positive_acc,
		eps_amt_diff_surp,
		sales_acceleration_curr_and_1_q_back,
		sales_sequential_positive_acc,
		sales_surprise,
		rs_pct_chg_nasdaq,
	} = screenerData ?? {};

	const hasAnyData = useMemo(
		() =>
			act_rpt_date != null ||
			eps_growth?.street_q_to_4q_back != null ||
			eps_growth?.street_curr_y_to_1_y_back != null ||
			combined_acceleration_curr_and_1_q_back?.street != null ||
			eps_amt_diff_surp != null ||
			sales_growth?.sales_q_to_4q_back != null ||
			sales_growth?.sales_curr_y_to_1_y_back != null ||
			sales_acceleration_curr_and_1_q_back != null ||
			sales_surprise?.q != null ||
			rs_pct_chg_nasdaq?.ed != null ||
			rs_pct_chg_nasdaq?.["90d"] != null,
		[
			act_rpt_date,
			eps_growth?.street_q_to_4q_back,
			eps_growth?.street_curr_y_to_1_y_back,
			combined_acceleration_curr_and_1_q_back?.street,
			eps_amt_diff_surp,
			sales_growth?.sales_q_to_4q_back,
			sales_growth?.sales_curr_y_to_1_y_back,
			sales_acceleration_curr_and_1_q_back,
			sales_surprise?.q,
			rs_pct_chg_nasdaq?.ed,
			rs_pct_chg_nasdaq?.["90d"],
		],
	);

	if (!hasAnyData) return null;

	return (
		<div className={componentClassNames} data-testid="earnings-table">
			<LineHeader title="Earnings" />

			{act_rpt_date != null && (
				<QuoteRow left="Earnings Date" right={formatValueDate(act_rpt_date)} />
			)}
			{eps_growth?.street_q_to_4q_back != null && (
				<QuoteRow
					left="EPS %chg QYoY"
					right={formatPositivePercentValue(eps_growth.street_q_to_4q_back)}
					className={cn(
						valueColor(eps_growth.street_q_to_4q_back),
						"font-bold",
					)}
				/>
			)}
			{combined_acceleration_curr_and_1_q_back?.street != null && (
				<QuoteRow
					left="EPS Acceleration"
					right={
						<>
							<span
								className={cn(
									valueColor(combined_acceleration_curr_and_1_q_back.street),
									"font-bold",
								)}
							>
								{formatPositivePercentValue(
									combined_acceleration_curr_and_1_q_back.street,
								)}
							</span>{" "}
							{combined_sequential_positive_acc?.street != null && (
								<span>#Qtr {combined_sequential_positive_acc.street}</span>
							)}
						</>
					}
				/>
			)}
			{eps_growth?.street_curr_y_to_1_y_back != null && (
				<QuoteRow
					left="Annual EPS Gr"
					right={formatPositivePercentValue(
						eps_growth.street_curr_y_to_1_y_back,
					)}
					className={cn(
						valueColor(eps_growth.street_curr_y_to_1_y_back),
						"font-bold",
					)}
				/>
			)}
			{eps_amt_diff_surp != null && (
				<QuoteRow
					left="Surprise EPS"
					right={
						<span className={cn(valueColor(eps_amt_diff_surp), "font-bold")}>
							{formatEps(eps_amt_diff_surp)}{" "}
							{formatPositivePercentValue(eps_amt_diff_surp)}
						</span>
					}
				/>
			)}
			{sales_growth?.sales_q_to_4q_back != null && (
				<QuoteRow
					left="Sales %chg QYoY"
					right={formatPositivePercentValue(sales_growth.sales_q_to_4q_back)}
					className={cn(
						valueColor(sales_growth.sales_q_to_4q_back),
						"font-bold",
					)}
				/>
			)}
			{sales_acceleration_curr_and_1_q_back != null && (
				<QuoteRow
					left="Sales Acceleration"
					right={
						<>
							<span
								className={cn(
									valueColor(sales_acceleration_curr_and_1_q_back),
									"font-bold",
								)}
							>
								{formatPositivePercentValue(
									sales_acceleration_curr_and_1_q_back,
								)}
							</span>{" "}
							{sales_sequential_positive_acc != null && (
								<span>#Qtr {sales_sequential_positive_acc}</span>
							)}
						</>
					}
				/>
			)}
			{sales_growth?.sales_curr_y_to_1_y_back != null && (
				<QuoteRow
					left="Annual Sales Gr"
					right={formatPositivePercentValue(
						sales_growth.sales_curr_y_to_1_y_back,
					)}
					className={cn(
						valueColor(sales_growth.sales_curr_y_to_1_y_back),
						"font-bold",
					)}
				/>
			)}
			{sales_surprise?.q != null && (
				<QuoteRow
					left="Surprise Sales"
					right={
						<span className={valueColor(sales_surprise.q)}>
							{formatEps(sales_surprise.q)}{" "}
							{formatPositivePercentValue(sales_surprise.q)}
						</span>
					}
					className={cn(valueColor(sales_surprise.q), "font-bold")}
				/>
			)}

			{rs_pct_chg_nasdaq?.ed != null || rs_pct_chg_nasdaq?.["90d"] != null ? (
				<QuoteRow
					left="Revisions Score"
					right={
						<>
							{rs_pct_chg_nasdaq?.ed != null && (
								<>
									<span
										className={cn(
											valueColor(rs_pct_chg_nasdaq.ed),
											"font-bold",
										)}
									>
										{formatPositivePercentValue(rs_pct_chg_nasdaq.ed)}
									</span>{" "}
									(ED)
								</>
							)}
							{rs_pct_chg_nasdaq?.ed != null &&
								rs_pct_chg_nasdaq?.["90d"] != null &&
								" / "}
							{rs_pct_chg_nasdaq?.["90d"] != null && (
								<>
									<span
										className={cn(
											valueColor(rs_pct_chg_nasdaq["90d"]),
											"font-bold",
										)}
									>
										{formatPositivePercentValue(rs_pct_chg_nasdaq["90d"])}{" "}
									</span>
									(90d)
								</>
							)}
						</>
					}
				/>
			) : null}
		</div>
	);
};
